import { createAsyncThunk, createSlice, SerializedError } from '@reduxjs/toolkit';
import { isFetching, error } from './common';
import { CommonRootState } from '../combineReducers';
import { fetchAll } from '@pec/aion-ui-odata/helpers/fetchAll';

/**
 * Represents a unique client that a subcontractor is connected to via SCM subcontracts.
 * Derived by aggregating clientSubcontracts across all subcontracts and deduplicating by clientId.
 * CPRO-4322: Used for Pendo metadata.
 */
export interface IScmConnectedClient {
  clientId: string;
  clientName: string;
}

interface IClientSubcontractResponse {
  clientId: string;
  clientName: string;
}

interface ISubcontractResponse {
  clientSubcontracts: IClientSubcontractResponse[];
}

const name = 'scmConnectedClients';

/**
 * Fetches the list of connected clients for a given contractor organization.
 * CPRO-4322: Uses $expand=ClientSubcontracts to load client subcontracts in a single request.
 *   1. GET subcontracts for the organization (isPrime=false) with $expand=ClientSubcontracts
 *   2. Deduplicate clients across all subcontracts
 * Only fetches if not already fetching or loaded.
 */
export const fetchScmConnectedClients = createAsyncThunk(
  name,
  async (organizationId: string) => {
    const subcontractsResponse = await fetchAll<ISubcontractResponse>(
      `/api/v3.01/subcontractorManagement/subcontracts`,
      {
        params: {
          orgIdFilter: organizationId,
          isPrime: false,
          $expand: 'ClientSubcontracts'
        }
      }
    );

    // Flatten and deduplicate clients by clientId
    const clientMap = new Map<string, IScmConnectedClient>();

    for (const subcontract of subcontractsResponse.value) {
      for (const cs of subcontract.clientSubcontracts ?? []) {
        if (cs.clientId && !clientMap.has(cs.clientId)) {
          clientMap.set(cs.clientId, {
            clientId: cs.clientId,
            clientName: cs.clientName
          });
        }
      }
    }

    return Array.from(clientMap.values());
  },
  {
    condition(_, { getState }) {
      const { isFetching, isLoaded } = (getState() as CommonRootState).scmConnectedClients;
      return !isFetching && !isLoaded;
    }
  }
);

export type State = {
  isFetching: boolean;
  clients: IScmConnectedClient[];
  isLoaded: boolean;
  error: SerializedError | null;
};

export const initialState: State = {
  isFetching: false,
  clients: [],
  isLoaded: false,
  error: null
};

const scmConnectedClients = createSlice({
  name,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchScmConnectedClients.pending, isFetching);
    builder.addCase(fetchScmConnectedClients.fulfilled, (state, action) => {
      state.isFetching = false;
      state.clients = action.payload ?? [];
      state.isLoaded = true;
    });
    builder.addCase(fetchScmConnectedClients.rejected, error);
  }
});

export const { reducer: scmConnectedClientsReducer } = scmConnectedClients;
